import { useState } from 'react';
import DatePickerDD from '@/components/DatePickerDD';
import BankSearchModal from '@/components/admin/BankSearchModal';
import MarkOrderPaidModal from '@/components/admin/MarkOrderPaidModal';
import RegisterOrphanPaymentModal from '@/components/admin/RegisterOrphanPaymentModal';
import { Search, CheckCircle2, PlusCircle } from 'lucide-react';

export default function OrderManagementTab({ orders, merchants, orderFilters, setOrderFilters, onReload, setToast }) {
  const [bankSearchOrder, setBankSearchOrder] = useState(null);
  const [markPaidOrder, setMarkPaidOrder] = useState(null);
  const [showOrphanModal, setShowOrphanModal] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Todas las Ordenes del Sistema</h2>
        <button
          onClick={() => setShowOrphanModal(true)}
          data-testid="register-orphan-payment-btn"
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          title="Registrar un pago recibido en el banco que no tiene una orden previa"
        >
          <PlusCircle className="w-4 h-4" /> Pago huérfano
        </button>
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <select value={orderFilters.status} onChange={(e) => { setOrderFilters({...orderFilters, status: e.target.value}); setTimeout(onReload, 100); }} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
            <option value="">Todos los Estados</option>
            <option value="paid">Pagadas</option>
            <option value="pending">Pendientes</option>
            <option value="expired">Expiradas</option>
            <option value="failed">Fallidas</option>
            <option value="cancelled">Canceladas</option>
          </select>
          <select value={orderFilters.paymentMethod} onChange={(e) => { setOrderFilters({...orderFilters, paymentMethod: e.target.value}); setTimeout(onReload, 100); }} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="order-method-filter">
            <option value="">Todos los Metodos</option>
            <option value="p2p">P2P</option>
            <option value="immediate_debit">Debito</option>
            <option value="bcv_transfer">Transfer</option>
            <option value="c2p">C2P</option>
            <option value="manual_reconciliation">Conciliación Manual</option>
          </select>
          <select value={orderFilters.project} onChange={(e) => { setOrderFilters({...orderFilters, project: e.target.value}); setTimeout(onReload, 100); }} className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
            <option value="">Todos los Proyectos</option>
            {merchants.flatMap(m => (m.projects || []).map(p => <option key={p.project_id} value={p.project_id}>{p.project_name} ({m.company_name})</option>))}
          </select>
          <DatePickerDD value={orderFilters.dateFrom ? (() => { const [y, m, d] = orderFilters.dateFrom.split('-'); return `${d}/${m}/${y}`; })() : ''} onChange={(val) => { if (val?.length === 10) { const [d, m, y] = val.split('/'); setOrderFilters({...orderFilters, dateFrom: `${y}-${m}-${d}`}); setTimeout(onReload, 100); } }} placeholder="Desde (DD/MM/YYYY)" />
          <DatePickerDD value={orderFilters.dateTo ? (() => { const [y, m, d] = orderFilters.dateTo.split('-'); return `${d}/${m}/${y}`; })() : ''} onChange={(val) => { if (val?.length === 10) { const [d, m, y] = val.split('/'); setOrderFilters({...orderFilters, dateTo: `${y}-${m}-${d}`}); setTimeout(onReload, 100); } }} placeholder="Hasta (DD/MM/YYYY)" />
          <button onClick={() => { setOrderFilters({ status: '', paymentMethod: '', project: '', dateFrom: '', dateTo: '', search: '' }); setTimeout(onReload, 100); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors">Limpiar Filtros</button>
        </div>
      </div>
      
      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Codigo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proyecto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metodo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {orders.map((order) => (
              <tr key={order.order_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-mono font-bold" style={{ color: '#003594' }}>{order.order_code}</td>
                <td className="px-6 py-4 text-sm">{order.project_name || 'N/A'}</td>
                <td className="px-6 py-4 text-sm">{order.payer_name}</td>
                <td className="px-6 py-4 text-sm font-semibold">{order.currency} {order.amount.toFixed(2)}</td>
                <td className="px-6 py-4">
                  {order.payment_method_used === 'manual_reconciliation' ? (
                    <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 font-medium whitespace-nowrap" data-testid={`method-manual-${order.order_code}`} title="Pago registrado por conciliación manual — no afecta el saldo del ledger (salvo que se haya activado)">
                      Conc. Manual
                    </span>
                  ) : order.payment_method_used ? (
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {order.payment_method_used === 'p2p' && 'P2P'}
                      {order.payment_method_used === 'immediate_debit' && 'Debito'}
                      {order.payment_method_used === 'bcv_transfer' && 'Transfer'}
                      {order.payment_method_used === 'c2p' && 'C2P'}
                    </span>
                  ) : <span className="text-xs text-gray-400">-</span>}
                </td>
                <td className="px-6 py-4"><span className={`px-2 py-1 text-xs rounded-full ${order.status === 'paid' ? 'bg-green-100 text-green-800' : order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{order.status}</span></td>
                <td className="px-6 py-4 text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString()}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setBankSearchOrder(order.order_code)}
                      data-testid={`bank-search-btn-${order.order_code}`}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                      title="Buscar movimientos bancarios reales para esta orden"
                    >
                      <Search className="w-3 h-3" /> Banco
                    </button>
                    {order.status !== 'paid' && order.status !== 'refunded' && (
                      <button
                        onClick={() => setMarkPaidOrder(order)}
                        data-testid={`mark-paid-btn-${order.order_code}`}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
                        title="Marcar esta orden como paid manualmente (reconciliación)"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Paid
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bankSearchOrder && (
        <BankSearchModal
          orderCode={bankSearchOrder}
          onClose={() => setBankSearchOrder(null)}
          onReconciled={() => { onReload?.(); }}
          setToast={setToast}
        />
      )}

      {markPaidOrder && (
        <MarkOrderPaidModal
          order={markPaidOrder}
          onClose={() => setMarkPaidOrder(null)}
          onSuccess={() => { onReload?.(); }}
          setToast={setToast}
        />
      )}

      {showOrphanModal && (
        <RegisterOrphanPaymentModal
          merchants={merchants}
          onClose={() => setShowOrphanModal(false)}
          onSuccess={() => { onReload?.(); }}
          setToast={setToast}
        />
      )}
    </div>
  );
}
